import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useToast } from "../../context/ToastContext";
import { useTranslation } from "react-i18next";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";

// Services & Context
import FailureApi from "../../services/FailureApi";
import FamilyFmeaApi from "../../services/FamilyFmeaApi";
import FmeaApi from "../../services/FmeaApi";
import ProductApi from "../../services/ProductApi";
import { useAuth } from "../../context/AuthContext";

// Design System
import { DataTable } from "../../components/DataTable/DataTable";
import FilterPanel from "../../components/FilterPanel/FilterPanel";
import Input from "../../components/Input/Input";
import { Button } from "../../components/Button/Button";
import { Tag } from "../../components/Tag/Tag";
import PageHeader from "../../components/PageHeader/PageHeader";
import type { PaginatorPageChangeEvent } from "../../components/Paginator/Paginator";
import { Column } from "primereact/column";
import { Checkbox } from "primereact/checkbox";

interface FailureRow {
  id: string;
  name: string;
  description: string;
  effect: string;
  operationName: string;
  severity: number;
  occurrence: number;
  detectability: number;
  rpn: number;
  causeId: string;
  causeDescription: string;
  plantId: string;
  operationId: string;
  fmeaTitle: string;
}

const SelectInheritedFailures: React.FC = () => {
  const { fmeaId: productFmeaId } = useParams<{ fmeaId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fmeaSource = searchParams.get("type") || "product";
  const { plantId } = useAuth();
  const { displayToast } = useToast();
  const { t } = useTranslation(["form", "General"]);

  const [familyFmeaIds, setFamilyFmeaIds] = useState<string[]>([]);
  const [failures, setFailures] = useState<FailureRow[]>([]);
  const [selectedFailures, setSelectedFailures] = useState<FailureRow[]>([]);
  const [importedFailureIds, setImportedFailureIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState<"asc"|"desc">("asc");

  // New filter states for FilterPanel
  const [filterFmeaTitle, setFilterFmeaTitle] = useState("");
  const [filterOp, setFilterOp] = useState("");
  const [filterDescription, setFilterDescription] = useState("");
  const [filterEffect, setFilterEffect] = useState("");
  const [filterSeverity, setFilterSeverity] = useState<number | null>(null);
  const [filterOccurrence, setFilterOccurrence] = useState<number | null>(null);
  const [filterDetectability, setFilterDetectability] = useState<number | null>(null);
  const [filterRPN, setFilterRPN] = useState<number | null>(null);

  useEffect(() => {
    if (!productFmeaId) {
      setInitialLoading(false);
      return;
    }
    let isMounted = true;
    const fetchFailuresList: any = async () => {
      setInitialLoading(true);
      try {

        const respProductFmea = await FmeaApi.GetById(productFmeaId);
        const prodFmea = respProductFmea;
        if (!prodFmea.productId) {
          displayToast("error", t("form:error"), t("form:familyFmeaNotSelected"), false, 3000);
          return;
        }

        const respProduct = await ProductApi.GetById(prodFmea.productId);
        const product = respProduct;
        if (!product.familyId) {
          displayToast("error", t("form:error"), t("form:familyFmeaNotSelected"), false, 3000);
          return;
        }

        const activeFmeas = await FamilyFmeaApi.GetActiveFamilyFmeasByFamilyId(product.familyId);
        const ffIds = activeFmeas.map((ff: any) => ff.id);
        if (isMounted) {
          setFamilyFmeaIds(ffIds);
        }
        // load list and then fetch already imported failures
        await loadFailures(ffIds, filters, page, sortField, sortOrder, rowsPerPage);
        try {
          const respImported = await FailureApi.GetImportedFailures(productFmeaId);
          const imported = respImported.data as Array<{ failureId: string; parentFailureId: string }>;
          if (isMounted) {
            setImportedFailureIds(new Set(imported.map(i => i.parentFailureId)));
          }
        } catch (e) {
          console.error("Error fetching imported failures", e);
        }
      } catch (err: any) {
        displayToast("error", t("form:error"), err.message ?? String(err), false, 3000);
      } finally {
        if (isMounted) {
          setInitialLoading(false);
        }
      }
    };
    fetchFailuresList();
    return () => {
      isMounted = false;
    };
  }, [productFmeaId]);


  const loadFailures: any = async (
    ffIds: string[],
    filters: Record<string, any> = {},
    firstIndex = 0,
    field = sortField,
    dir: "asc"|"desc" = sortOrder,
    size = rowsPerPage
  ) => {
    if (!ffIds || ffIds.length === 0) {
      return;
    }
    setLoading(true);
    try {
      const results = await Promise.all([
        FailureApi.GetByFilterAsync(
          [plantId],
          Math.floor(firstIndex / size) + 1,
          size,
          field,
          dir,
          filters.description?.value,
          filters.effect?.value,
          filters.causeIds?.value,
          filters.severity?.value,
          filters.occurrence?.value,
          filters.detectability?.value,
          filters.operationName?.value, // operationName
          undefined,                // version
          filters.rpn?.value,       // rpn
          undefined,                // code
          filters.fmeaTitle?.value, // fmeaTitle
          ffIds                     // fmeaIds array
        )
      ]);
      const merged = results.flatMap(resp =>
        Array.isArray(resp.data?.items)
          ? resp.data.items
          : Array.isArray(resp.data?.failures)
          ? resp.data.failures
          : []
      );
      setFailures(
        merged.map((fa: any) => ({
          ...fa,
          fmeaTitle: fa.fmeaTitle ?? ""
        }))
      );
      setTotalRecords(merged.length);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = (): void => {
    const newFilters: Record<string, any> = {
      fmeaTitle: filterFmeaTitle ? { value: filterFmeaTitle } : undefined,
      operationName: filterOp ? { value: filterOp } : undefined,
      description: filterDescription ? { value: filterDescription } : undefined,
      effect: filterEffect ? { value: filterEffect } : undefined,
      severity: filterSeverity !== null ? { value: filterSeverity } : undefined,
      occurrence: filterOccurrence !== null ? { value: filterOccurrence } : undefined,
      detectability: filterDetectability !== null ? { value: filterDetectability } : undefined,
      rpn: filterRPN !== null ? { value: filterRPN } : undefined,
    };

    Object.keys(newFilters).forEach(key => {
      if (newFilters[key] === undefined) delete newFilters[key];
    });

    setPage(0);
    setFilters(newFilters);
    loadFailures(familyFmeaIds, newFilters, 0, sortField, sortOrder, rowsPerPage);
  };

  const handleClearFilters = (): void => {
    setFilterFmeaTitle("");
    setFilterOp("");
    setFilterDescription("");
    setFilterEffect("");
    setFilterSeverity(null);
    setFilterOccurrence(null);
    setFilterDetectability(null);
    setFilterRPN(null);

    setPage(0);
    setFilters({});
    loadFailures(familyFmeaIds, {}, 0, sortField, sortOrder, rowsPerPage);
  };

  const onPage = (e: PaginatorPageChangeEvent): void => {
    setPage(e.first);
    setRowsPerPage(e.rows);
    loadFailures(familyFmeaIds, filters, e.first, sortField, sortOrder, e.rows);
  };

  const onSort = (e: any): void => {
    const dir = e.sortOrder === 1 ? "asc" : "desc";
    setSortField(e.sortField || "");
    setSortOrder(dir);
    loadFailures(familyFmeaIds, filters, page, e.sortField || "", dir, rowsPerPage);
  };

  const handleSave: any = async () => {
  if (!productFmeaId) return;
    setSaving(true);
    try {
      const payload = failures.map(f => ({
        failureId: f.id,
        apply: selectedFailures.some(sf => sf.id === f.id)
      }));
  await FamilyFmeaApi.ImportFailures(productFmeaId, payload);
      displayToast("success", t("form:Success"), t("form:failuresLinkUpdated"), false, 3000);
  navigate(`/fmeas/${productFmeaId}/failures?type=${fmeaSource}`);
    } catch (error: any) {
      displayToast("error", t("form:error"), error.message || String(error), false, 7000);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = (): void => {
    confirmDialog({
      message: t("form:cancelCreationMessage"),
      header: t("form:confirmation"),
      icon: "pi pi-exclamation-triangle",
      acceptLabel: t("form:Yes"),
      rejectLabel: t("form:No"),
      acceptClassName: "p-button-accept",
      rejectClassName: "p-button-reject",
      accept: () => navigate(`/fmeas/${productFmeaId}/failures?type=${fmeaSource}`),
      reject: () => {},
    });
  };

  return (
    <div className="overflow-hidden">
      <PageHeader
        title={t("form:inheritFailures")}
        description="Selecione as falhas da FMEA de família para herdar neste FMEA de produto"
      />
      
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <ConfirmDialog />
        
        <FilterPanel
          title="Filtros"
          showFilterIcon
          onApply={handleFilter}
          onClear={handleClearFilters}
        >
          <Input
            type="text"
            label={t("form:fmeaTitle")}
            value={filterFmeaTitle}
            onChange={(val) => setFilterFmeaTitle(val)}
          />
          <Input
            type="text"
            label={t("form:operation")}
            value={filterOp}
            onChange={(val) => setFilterOp(val)}
          />
          <Input
            type="text"
            label={t("form:description")}
            value={filterDescription}
            onChange={(val) => setFilterDescription(val)}
          />
          <Input
            type="text"
            label={t("form:effect")}
            value={filterEffect}
            onChange={(val) => setFilterEffect(val)}
          />
          <Input
            type="text"
            label={t("form:severity")}
            value={filterSeverity?.toString() || ""}
            onChange={(val) => setFilterSeverity(val ? Number(val) : null)}
          />
          <Input
            type="text"
            label={t("form:occurrence")}
            value={filterOccurrence?.toString() || ""}
            onChange={(val) => setFilterOccurrence(val ? Number(val) : null)}
          />
          <Input
            type="text"
            label={t("form:detectability")}
            value={filterDetectability?.toString() || ""}
            onChange={(val) => setFilterDetectability(val ? Number(val) : null)}
          />
          <Input
            type="text"
            label={t("form:rpn")}
            value={filterRPN?.toString() || ""}
            onChange={(val) => setFilterRPN(val ? Number(val) : null)}
          />
        </FilterPanel>

        <DataTable
          value={failures}
          paginated
          first={page}
          rows={rowsPerPage}
          totalRecords={totalRecords}
          onPageChange={onPage}
          onSort={onSort}
          sortField={sortField}
          sortOrder={sortOrder === "asc" ? 1 : -1}
          loading={loading || initialLoading}
          dataKey="id"
          emptyMessage={t("form:noRecordsFound") || "Nenhum registro encontrado"}
          rowsPerPageOptions={[10, 20, 50]}
          resizableColumns
        >
          <Column
            header={t("form:inherit")}
            body={(rowData: FailureRow) => {
              const isImported = importedFailureIds.has(rowData.id);
              return (
                <Checkbox
                  inputId={rowData.id}
                  checked={isImported || selectedFailures.some(f => f.id === rowData.id)}
                  disabled={isImported}
                  onChange={e => {
                    if (isImported) return;
                    if (e.checked) setSelectedFailures(prev => [...prev, rowData]);
                    else setSelectedFailures(prev => prev.filter(f => f.id !== rowData.id));
                  }}
                />
              );
            }}
            style={{ width: "4rem", textAlign: "center" }}
          />
          <Column field="fmeaTitle" header={t("form:fmeaTitle")} sortable style={{ minWidth: "12rem" }} />
          <Column field="operationName" header={t("form:operation")} sortable style={{ minWidth: "10rem" }} />
          <Column field="description" header={t("form:description")} sortable style={{ minWidth: "16rem" }} />
          <Column field="effect" header={t("form:effect")} sortable style={{ minWidth: "16rem" }} />
          <Column field="severity" header={t("form:severity")} sortable style={{ minWidth: "6rem" }} />
          <Column field="occurrence" header={t("form:occurrence")} sortable style={{ minWidth: "6rem" }} />
          <Column field="detectability" header={t("form:detectability")} sortable style={{ minWidth: "6rem" }} />
          <Column
            field="rpn"
            header={t("form:rpn")}
            sortable
            style={{ minWidth: "6rem" }}
            body={(row: FailureRow) => {
              const value = row.rpn ?? 0;
              let severity: "successInverse" | "warningInverse" | "errorInverse" = "successInverse";
              if (value > 39 && value <= 100) severity = "warningInverse";
              else if (value > 100) severity = "errorInverse";
              
              return (
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <Tag label={value.toString()} severity={severity} />
                </div>
              );
            }}
          />
          <Column
            header={t("form:status")}
            body={(row: FailureRow) => {
              const isImported = importedFailureIds.has(row.id);
              if (isImported) {
                return (
                  <Tag label="Já Herdada" severity="primary" />
                );
              }
              return null;
            }}
            style={{ minWidth: "8rem", textAlign: "center" }}
          />
        </DataTable>

        <div className="flex justify-content-between align-items-center mt-4 flex-wrap gap-3">
          <Button
            label={t("form:Cancel")}
            variant="outlined"
            severity="danger"
            onClick={handleCancel}
            disabled={saving}
          />
          <Button
            label={t("form:save")}
            onClick={handleSave}
            disabled={saving || (selectedFailures.length === 0)}
            loading={saving}
          />
        </div>
      </div>
    </div>
  );
};

export default SelectInheritedFailures;